import React, { useState, useEffect } from 'react';
import './StaffTeam.css';
import { getUsers } from '../utils/storage';
import { getRankMeta } from '../utils/rankConfig';

function StaffTeam({ onViewProfile }) {
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    loadStaff();
    // Listen for storage changes to update staff list when roles change
    const handleStorageChange = () => {
      loadStaff();
    };
    window.addEventListener('storage', handleStorageChange);
    // Also check periodically
    const interval = setInterval(loadStaff, 2000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const loadStaff = () => {
    const users = getUsers();
    // Filter staff roles (exclude regular users)
    const staffRoles = ['founder', 'co-founder', 'manager', 'administrator', 'head-admin', 'admin', 'mod', 'partner', 'advertiser', 'sponsor'];
    const staffMembers = users.filter(user => staffRoles.includes(user.role) && !user.locked);
    
    // Sort by role priority and then by join date
    const rolePriority = {
      'founder': 1,
      'co-founder': 2,
      'manager': 3,
      'administrator': 4,
      'head-admin': 5,
      'admin': 6,
      'mod': 7,
      'partner': 8,
      'advertiser': 9,
      'sponsor': 10
    };
    
    staffMembers.sort((a, b) => {
      const priorityA = rolePriority[a.role] || 99;
      const priorityB = rolePriority[b.role] || 99;
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
    
    setStaff(staffMembers);
  };

  const getRoleDisplayName = (role) => getRankMeta(role).name || role;

  const getRoleCategory = (role) => {
    if (role === 'founder' || role === 'co-founder') {
      return 'founders';
    } else if (role === 'manager' || role === 'administrator' || role === 'head-admin') {
      return 'management';
    } else if (role === 'admin' || role === 'mod') {
      return 'staff';
    } else {
      return 'partners';
    }
  };

  const groupStaffByCategory = () => {
    const categories = {
      founders: [],
      management: [],
      staff: [],
      partners: []
    };

    staff.forEach(member => {
      const category = getRoleCategory(member.role);
      categories[category].push(member);
    });

    return categories;
  };

  const categories = groupStaffByCategory();

  const getRoleColor = (role) => getRankMeta(role).color || '#aab1c7';

  return (
    <div className="staff-team-page">
      <div className="staff-header">
        <h1 className="section-title">👥 Staff Team</h1>
        <p className="staff-intro">
          Meet our amazing team members who help keep NitroDrop running smoothly!
        </p>
      </div>

      {staff.length === 0 ? (
        <div className="no-staff">
          <p>No staff members yet. Check back soon!</p>
        </div>
      ) : (
        <div className="staff-categories">
          {/* Founders */}
          {categories.founders.length > 0 && (
            <div className="staff-category">
              <h2 className="category-title founders-title">
                <span className="category-icon">👑</span>
                Founders
              </h2>
              <div className="staff-grid">
                {categories.founders.map(member => (
                  <div key={member.id} className="staff-card founders-card" onClick={() => onViewProfile && onViewProfile(member.id)} style={{ cursor: onViewProfile ? 'pointer' : 'default' }}>
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.discordUsername} className="staff-avatar-img" />
                    ) : (
                      <div className="staff-avatar" style={{ backgroundColor: getRoleColor(member.role) }}>
                        {member.discordUsername.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="staff-info">
                      <h3>{member.discordUsername}</h3>
                      <span className="staff-role" style={{ color: getRoleColor(member.role) }}>
                        {getRoleDisplayName(member.role)}
                      </span>
                      <small className="staff-joined">
                        Joined {new Date(member.createdAt).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Management */}
          {categories.management.length > 0 && (
            <div className="staff-category">
              <h2 className="category-title management-title">
                <span className="category-icon">💼</span>
                Management
              </h2>
              <div className="staff-grid">
                {categories.management.map(member => (
                  <div key={member.id} className="staff-card management-card" onClick={() => onViewProfile && onViewProfile(member.id)} style={{ cursor: onViewProfile ? 'pointer' : 'default' }}>
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.discordUsername} className="staff-avatar-img" />
                    ) : (
                      <div className="staff-avatar" style={{ backgroundColor: getRoleColor(member.role) }}>
                        {member.discordUsername.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="staff-info">
                      <h3>{member.discordUsername}</h3>
                      <span className="staff-role" style={{ color: getRoleColor(member.role) }}>
                        {getRoleDisplayName(member.role)}
                      </span>
                      <small className="staff-joined">
                        Joined {new Date(member.createdAt).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Staff */}
          {categories.staff.length > 0 && (
            <div className="staff-category">
              <h2 className="category-title staff-title">
                <span className="category-icon">⚙️</span>
                Staff
              </h2>
              <div className="staff-grid">
                {categories.staff.map(member => (
                  <div key={member.id} className="staff-card staff-card-basic" onClick={() => onViewProfile && onViewProfile(member.id)} style={{ cursor: onViewProfile ? 'pointer' : 'default' }}>
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.discordUsername} className="staff-avatar-img" />
                    ) : (
                      <div className="staff-avatar" style={{ backgroundColor: getRoleColor(member.role) }}>
                        {member.discordUsername.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="staff-info">
                      <h3>{member.discordUsername}</h3>
                      <span className="staff-role" style={{ color: getRoleColor(member.role) }}>
                        {getRoleDisplayName(member.role)}
                      </span>
                      <small className="staff-joined">
                        Joined {new Date(member.createdAt).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Partners */}
          {categories.partners.length > 0 && (
            <div className="staff-category">
              <h2 className="category-title partners-title">
                <span className="category-icon">🤝</span>
                Partners & Sponsors
              </h2>
              <div className="staff-grid">
                {categories.partners.map(member => (
                  <div key={member.id} className="staff-card partners-card" onClick={() => onViewProfile && onViewProfile(member.id)} style={{ cursor: onViewProfile ? 'pointer' : 'default' }}>
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.discordUsername} className="staff-avatar-img" />
                    ) : (
                      <div className="staff-avatar" style={{ backgroundColor: getRoleColor(member.role) }}>
                        {member.discordUsername.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="staff-info">
                      <h3>{member.discordUsername}</h3>
                      <span className="staff-role" style={{ color: getRoleColor(member.role) }}>
                        {getRoleDisplayName(member.role)}
                      </span>
                      <small className="staff-joined">
                        Joined {new Date(member.createdAt).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StaffTeam;

